import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Link2, Database, Cloud, Shield, Zap, Calculator } from 'lucide-react';
import { integrationLogos, ehrLogos } from '../components/AssetPlaceholder';
import IntegrationAnimation from '../components/IntegrationAnimation';
import ROICalculator from '../components/ROICalculator';

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
};

const integrationCategories = [
  {
    name: 'Electronic Health Records',
    description: 'Seamless bidirectional sync with all major EHR systems via FHIR R4',
    icon: Database,
    integrations: ['Epic', 'Cerner', 'athenahealth', 'Allscripts', 'eClinicalWorks', 'NextGen', 'Meditech', 'DrChrono'],
  },
  {
    name: 'Practice Management',
    description: 'Connect your scheduling, billing, and patient communication tools',
    icon: Cloud,
    integrations: ['Kareo', 'AdvancedMD', 'Practice Fusion', 'CareCloud', 'Greenway'],
  },
  {
    name: 'Billing & Revenue Cycle',
    description: 'Integrate with clearinghouses and payer portals',
    icon: Link2,
    integrations: ['Availity', 'Change Healthcare', 'Waystar', 'Trizetto', 'Optum'],
  },
  {
    name: 'Labs & Diagnostics',
    description: 'Receive lab results and diagnostic reports automatically',
    icon: Zap,
    integrations: ['Quest Diagnostics', 'LabCorp', 'BioReference', 'ARUP'],
  },
];

const integrationFeatures = [
  {
    title: 'FHIR R4 Native Architecture',
    description: 'AriaOne is built from the ground up on HL7 FHIR R4 — the modern healthcare interoperability standard. Patient, Encounter, Observation, Claim, and Schedule resources sync bidirectionally in real-time.',
    icon: Shield,
    details: ['SMART on FHIR authorization', 'Bulk FHIR data export', 'CDS Hooks for real-time decision support'],
  },
  {
    title: 'Real-Time Sync',
    description: 'Changes in your EHR appear in AriaOne instantly. Webhooks, polling, and event-driven architecture ensure zero data lag.',
    icon: Zap,
    details: ['Sub-second data propagation', 'Webhook-based event notifications', 'Conflict resolution for concurrent edits'],
  },
  {
    title: 'Bidirectional Data Flow',
    description: 'Notes, codes, and orders flow back to your EHR with a single click. SOAP notes from Lumina, CPT codes from RevQ, and appointment changes from Echo write back seamlessly.',
    icon: Link2,
    details: ['Write-back for clinical notes', 'Push CPT/ICD codes to billing', 'Sync appointment status changes'],
  },
  {
    title: 'Lumina Lens Bridge',
    description: "No FHIR endpoint? No problem. Lumina Lens scans documents, faxes, and PDFs from your existing system and structures the data automatically.",
    icon: Database,
    details: ['OCR with medical NLP', 'Structured extraction from PDFs', 'Automatic data classification'],
  },
];

// Adoption phases (same as homepage)
const adoptionPhases = [
  { 
    name: 'Assist', subtitle: 'Low Friction', color: '#aaaaaa',
    description: 'Zero risk to your current workflow. AriaOne runs alongside your EHR as a read-only companion.',
    features: ['Read-only — view patient data, no writes to your EHR', 'AI generates suggestions for coding, billing, scheduling', 'Copy/paste workflow — take what you need back to your EHR', 'Lens uploads — scan docs from your EHR into AriaOne', 'No risk — your existing system stays untouched'],
  },
  { 
    name: 'Semi-Automation', subtitle: 'Smart Assist', color: '#8b7cf0',
    description: 'AriaOne starts doing the heavy lifting. Pre-filled notes, suggested codes, structured outputs.',
    features: ['Pre-fill SOAP notes from ambient capture', 'AI suggests CPT/ICD codes with confidence scores', 'Structured outputs ready to paste into your EHR', 'Save 2-3 hours/day on documentation'],
  },
  { 
    name: 'Deep Integration', subtitle: 'Connected', color: '#a99cf5',
    description: 'AriaOne connects to your EHR via FHIR. Partial write-back, workflow automation, bi-directional sync.',
    features: ['FHIR-based connection to your EHR', 'Partial write-back — push notes, codes, claims', 'Workflow automation across billing & scheduling', 'Full audit trail and HIPAA compliance'],
  },
  { 
    name: 'Full Migration', subtitle: 'Optional', color: '#c4bbf7',
    description: "When you're ready, Lumina + RevQ + Echo = your complete healthcare platform.",
    features: ['Lumina + RevQ + Echo as your unified platform', 'Enable Healthcare native integration — full sync', 'Single source of truth for all clinical & billing data', "NOT required — only when you're ready"],
  },
];

const HeroSection = () => (
  <section className="relative min-h-[70vh] flex items-center hero-gradient overflow-hidden">
    <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 py-24 md:py-32">
      <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center">
        <motion.p variants={fadeInUp} className="text-[#888] text-xs font-medium tracking-widest uppercase mb-5">
          Integrations
        </motion.p>
        <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl text-white font-medium tracking-tight max-w-[800px] mx-auto leading-[1.1]">
          Connects to everything. Replaces nothing.
        </motion.h1>
        <motion.p variants={fadeInUp} className="text-aria-text-secondary text-base md:text-lg lg:text-xl max-w-[600px] mx-auto mt-6 leading-relaxed">
          AriaOne works alongside your existing systems. FHIR R4 native, bidirectional sync with every major EHR, PM, and billing platform.
        </motion.p>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-12">
        <IntegrationAnimation />
      </motion.div>
    </div>
  </section>
);

const EnableHealthcareSection = () => (
  <section className="py-16 page-gradient">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
      <motion.div 
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
        className="rounded-2xl p-8 md:p-12 text-center"
        style={{ background: 'linear-gradient(135deg, rgba(107,92,231,0.1), rgba(107,92,231,0.03))', border: '1px solid rgba(107,92,231,0.2)' }}
        data-testid="enable-healthcare-section"
      >
        <img src="/screenshots/enable-healthcare-emblem_wordmark_white.png" alt="Enable Healthcare" className="h-12 object-contain mx-auto mb-6" />
        <h2 className="text-2xl md:text-3xl text-white font-medium mb-3">Natively Integrated with Enable Healthcare</h2>
        <p className="text-aria-text-secondary text-base max-w-2xl mx-auto leading-relaxed">
          AriaOne and all its apps — Lumina, RevQ, and Echo — are natively integrated with Enable Healthcare's EHR, 
          Practice Management system, and Billing solutions. Zero configuration required. Full bi-directional sync, 
          single sign-on, and unified patient records out of the box.
        </p>
        <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
          {['EHR', 'Practice Management', 'Billing'].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-[#888]" />
              <span className="text-white">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

const FHIRSection = () => (
  <section className="py-24 md:py-32 section-gradient">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
        <motion.p variants={fadeInUp} className="text-[#888] text-xs font-medium tracking-widest uppercase mb-4">FHIR R4 Native</motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl text-white font-medium tracking-tight">
          Built for healthcare interoperability
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-aria-text-secondary text-base max-w-2xl mx-auto mt-4">
          AriaOne implements HL7 FHIR R4 as its core data layer. Every resource — Patient, Encounter, Observation, DiagnosticReport, 
          Claim, and Appointment — maps directly to FHIR standards, enabling seamless data exchange with any FHIR-compliant system.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrationFeatures.map((feature) => (
          <motion.div key={feature.title} variants={fadeInUp} className="bento-card p-8">
            <feature.icon className="w-10 h-10 text-[#888] mb-4" />
            <h3 className="text-xl text-white font-medium mb-2">{feature.title}</h3>
            <p className="text-aria-text-secondary text-sm leading-relaxed mb-4">{feature.description}</p>
            <div className="space-y-2">
              {feature.details.map((detail) => (
                <div key={detail} className="flex items-center gap-2 text-xs text-aria-text-muted">
                  <Check className="w-3.5 h-3.5 text-[#888] flex-shrink-0" />
                  <span>{detail}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const CategoriesSection = () => (
  <section className="py-24 md:py-32 page-gradient">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
      {integrationCategories.map((category) => (
        <motion.div key={category.name} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="mb-16 last:mb-0">
          <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
              <category.icon className="w-6 h-6 text-[#888]" />
            </div>
            <div>
              <h2 className="text-2xl text-white font-medium">{category.name}</h2>
              <p className="text-aria-text-secondary text-sm">{category.description}</p>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {category.integrations.map((integration) => (
              <motion.div key={integration} variants={fadeInUp} className="integration-card" data-testid={`integration-cat-${integration.toLowerCase().replace(/\s/g, '-')}`}>
                <span className="text-white text-base font-medium">{integration}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// Adoption Journey on Integrations page
const AdoptionJourneySection = () => {
  const [activePhase, setActivePhase] = useState(0);
  
  return (
    <section className="py-24 md:py-32 section-gradient" data-testid="integrations-adoption">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
          <motion.p variants={fadeInUp} className="text-[#888] text-xs font-medium tracking-widest uppercase mb-4">Your Adoption Journey</motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl text-white font-medium tracking-tight">
            Integrate at your own pace
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-aria-text-secondary text-base max-w-xl mx-auto mt-4">
            Move from read-only companion to full platform migration — each phase builds on the last. No pressure, no lock-in.
          </motion.p>
        </motion.div>

        <div className="flex justify-center gap-3 mb-10">
          {adoptionPhases.map((phase, idx) => (
            <button
              key={idx}
              onClick={() => setActivePhase(idx)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                activePhase === idx ? 'bg-white text-black' : 'bg-aria-card border border-aria-border text-aria-text-secondary hover:border-white/30 hover:text-white'
              }`}
              data-testid={`intg-phase-pill-${idx}`}
            >
              {phase.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activePhase}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start"
          >
            <div>
              <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: `${adoptionPhases[activePhase].color}15`, color: adoptionPhases[activePhase].color, border: `1px solid ${adoptionPhases[activePhase].color}30` }}>
                Phase {activePhase + 1} — {adoptionPhases[activePhase].subtitle}
              </span>
              <h3 className="text-3xl text-white font-medium mt-4">{adoptionPhases[activePhase].name}</h3>
              <p className="text-aria-text-secondary text-base mt-3 leading-relaxed">{adoptionPhases[activePhase].description}</p>
            </div>
            <div className="bento-card p-6 space-y-3">
              {adoptionPhases[activePhase].features.map((feature, idx) => (
                <div key={feature} className="flex items-start gap-3" data-testid={`intg-feature-${activePhase}-${idx}`}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${adoptionPhases[activePhase].color}20` }}>
                    <Check className="w-3 h-3" style={{ color: adoptionPhases[activePhase].color }} />
                  </div>
                  <span className="text-aria-text-secondary text-sm leading-relaxed">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="py-20 md:py-24 hero-gradient">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 text-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl text-white font-medium tracking-tight">
          Don't see your system?
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-aria-text-secondary text-base md:text-lg max-w-[500px] mx-auto mt-4">
          Our team can build custom FHIR integrations for your specific workflow. Or use Lumina Lens to bridge any gap.
        </motion.p>
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          <button className="btn-primary px-8 h-12 rounded-full text-white text-sm font-medium">Request custom integration</button>
          <button className="btn-ghost px-8 h-12 rounded-full text-white text-sm font-medium">View API docs</button>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const IntegrationsPage = () => {
  const [roiOpen, setRoiOpen] = useState(false);

  return (
    <div data-testid="integrations-page" className="page-gradient">
      <HeroSection />
      <EnableHealthcareSection />
      <FHIRSection />
      <AdoptionJourneySection />
      <CategoriesSection />
      {/* ROI Calculator Button */}
      <section className="py-12 page-gradient">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <button
              onClick={() => setRoiOpen(true)}
              className="btn-primary px-8 h-12 rounded-xl text-white text-sm font-medium inline-flex items-center gap-3 mx-auto"
              data-testid="roi-calculator-btn"
            >
              <Calculator className="w-5 h-5" />
              Project Your ROI
            </button>
            <p className="text-aria-text-muted text-xs mt-3">Estimate your return based on practice size and selected apps</p>
          </motion.div>
        </div>
      </section>
      <CTASection />
      <ROICalculator isOpen={roiOpen} onClose={() => setRoiOpen(false)} />
    </div>
  );
};

export default IntegrationsPage;
